<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { isPhoneValid } from '@nova/core';

import NextStep from '@/components/NextStep.vue';
import { useWarehouseInfo } from '@/composables/useWarehouseInfo';
import { CheckoutFlowStep, useCheckoutFlow } from '@/composables/useCheckoutFlow';
import checkoutService from '@/services/checkoutService';
import useNotify from '@/composables/useNotify';

const form = ref();
const licensePlate = ref('');
const phoneNumber = ref('');
const isFormValid = ref(false);

const { warehouseData } = useWarehouseInfo();
const checkoutFlow = useCheckoutFlow();
const { notify } = useNotify();

const checkoutIdentifier = computed(
  () => warehouseData.value?.settings?.checkoutIdentifier || 'Any'
);

const rules = {
  licensePlate: (value: string) => {
    // OR logic: if checkoutIdentifier is Any and phone number is valid, license plate can be empty
    if (checkoutIdentifier.value === 'Any' && phoneNumber.value) {
      if (value && value.length < 8) return 'License plate must be at least 8 characters';
      return true;
    }

    if (!value) return 'License plate is required';
    if (value.length < 8) return 'License plate must be at least 8 characters';
    return true;
  },
  phoneNumber: (value: string) => {
    // OR logic: if checkoutIdentifier is Any and license plate is valid, phone can be empty
    if (checkoutIdentifier.value === 'Any' && licensePlate.value) {
      if (value && !isPhoneValid(value)) return 'Please enter a valid phone number';
      return true;
    }

    if (!value) return 'Phone number is required';
    if (!isPhoneValid(value)) return 'Please enter a valid phone number';
    return true;
  }
};

watch([licensePlate, phoneNumber, checkoutIdentifier], async () => {
  if (checkoutIdentifier.value === 'Any' && form.value) {
    await form.value.resetValidation();
    await form.value.validate();
  }
});

watch(isFormValid, async newVal => {
  checkoutFlow.setStepValid(CheckoutFlowStep.ValidationPage, newVal);
});

const handleSubmit = async () => {
  checkoutFlow.setIsStepLoading(true);
  try {
    if (!warehouseData.value?.id) {
      throw new Error('Warehouse data is not available');
    }

    const validationResponse = await checkoutService.validateCheckoutForm(warehouseData.value?.id, {
      licensePlate: licensePlate.value,
      phoneNumber: phoneNumber.value
    });

    checkoutFlow.setAppointmentId(validationResponse.appointmentId);
    checkoutFlow.setAssetVisitId(validationResponse.assetVisitId);

    checkoutFlow.setNextStep(CheckoutFlowStep.ViewBolPage);
  } catch (error) {
    let errorMessage = 'An error occurred during validation, please try again';

    if (typeof error?.response?.data?.message === 'string') {
      errorMessage = error.response.data.message;
    }

    notify(errorMessage, 'error');
    console.error('Validation submission failed:', error);
  } finally {
    checkoutFlow.setIsStepLoading(false);
  }
};
</script>

<template>
  <div class="d-flex flex-column gap-6 px-4 fill-height">
    <v-btn
      variant="text"
      prepend-icon="mdi-arrow-left"
      style="align-self: flex-start"
      @click="checkoutFlow.setPreviousStep(CheckoutFlowStep.WelcomePage)">
      Back
    </v-btn>

    <h3 class="heading-md-bold">Enter Truck's License Plate and Driver's Phone Number</h3>

    <v-form ref="form" @submit.prevent="handleSubmit" v-model="isFormValid" fast-fail>
      <div class="checkout-form-body d-flex flex-column gap-3">
        <div
          class="d-flex flex-column gap-2 form-field"
          v-if="checkoutIdentifier === 'Any' || checkoutIdentifier === 'License Plate'">
          <v-label class="typography-body-sm-bold required-field">Truck's License Plate</v-label>
          <v-text-field
            v-model="licensePlate"
            variant="outlined"
            density="default"
            :rules="[rules.licensePlate]"
            placeholder="e.g. 215BG2"
            validate-on="input"></v-text-field>
        </div>

        <div
          class="d-flex flex-column gap-2 form-field"
          v-if="checkoutIdentifier === 'Any' || checkoutIdentifier === 'Phone Number'">
          <v-label class="typography-body-sm-bold required-field">Driver's Phone Number</v-label>
          <v-text-field
            v-model="phoneNumber"
            variant="outlined"
            density="default"
            :rules="[rules.phoneNumber]"
            placeholder="+1 (555) 123-4567"
            type="tel"
            validate-on="input"></v-text-field>
        </div>
      </div>
    </v-form>
  </div>

  <next-step
    :current-step="checkoutFlow.step"
    :is-step-valid="checkoutFlow.isStepValid(checkoutFlow.step.value)"
    :on-step-complete="handleSubmit" />
</template>

<style scoped lang="scss">
.checkout-form-body {
  border-radius: 4px;
  border: 1px solid $color-line-border;
  padding: $spacing-4;
}

.checkout-form-body .form-field {
  .v-input {
    margin-top: 0;
  }

  .v-label {
    position: relative;
    color: $color-text-secondary;
    align-self: flex-start;
    opacity: 1;
  }
}

.checkout-form-body .form-field .required-field {
  &:after {
    content: '*';
    color: $color-text-error;
  }
}
</style>
